import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { money } from "@/lib/format";
import { PageHeader, Loader } from "@/components/common/Bits";
import { cn } from "@/lib/utils";
import { Users, Receipt, Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import ConfirmDialog from "../components/common/ConfirmDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const STATUS_STYLE = {
  available: {
    ring: "border-emerald-200",
    bg: "bg-white",
    dot: "bg-emerald-500",
    label: "Available",
    text: "text-emerald-600",
  },
  occupied: {
    ring: "border-orange-300",
    bg: "bg-orange-50",
    dot: "bg-orange-500",
    label: "Occupied",
    text: "text-orange-600",
  },
  reserved: {
    ring: "border-violet-300",
    bg: "bg-violet-50",
    dot: "bg-violet-500",
    label: "Reserved",
    text: "text-violet-600",
  },
  billing: {
    ring: "border-sky-300",
    bg: "bg-sky-50",
    dot: "bg-sky-500",
    label: "Billing",
    text: "text-sky-600",
  },
  cleaning: {
    ring: "border-stone-300",
    bg: "bg-stone-100",
    dot: "bg-stone-400",
    label: "Cleaning",
    text: "text-stone-500",
  },
};

export default function Tables() {
  const { branchId, branches, subscribe } = useApp();
  const activeBranch = branchId === "all" ? branches[0]?.id : branchId;
  const [tables, setTables] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [deleteTable, setDeleteTable] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [newTable, setNewTable] = useState({
    name: "",
    floor: "Main Floor",
    seats: 2,
  });

  const [adding, setAdding] = useState(false);
  const navigate = useNavigate();

  const saveTable = async () => {
    if (!newTable.name.trim()) {
      toast.error("Table name is required");
      return;
    }

    if (Number(newTable.seats) < 1) {
      toast.error("Seats must be at least 1");
      return;
    }

    if (!activeBranch) {
      toast.error("No active branch selected");
      return;
    }

    setAdding(true);

    try {
      const body = {
        name: newTable.name.trim(),
        floor: newTable.floor.trim() || "Main Floor",
        seats: Number(newTable.seats),
      };

      if (editingTable) {
        await api.updateTable(editingTable.id, body);
        toast.success("Table updated");
      } else {
        await api.createTable({
          branch_id: activeBranch,
          ...body,
        });
        toast.success("Table added");
      }

      setAddOpen(false);
      setEditingTable(null);
      setNewTable({
        name: "",
        floor: "Main Floor",
        seats: 2,
      });

      load();
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
          (editingTable ? "Failed to update table" : "Failed to add table"),
      );
    } finally {
      setAdding(false);
    }
  };

  const load = () =>
    activeBranch &&
    api
      .tables(activeBranch)
      .then((r) => setTables(r.data))
      .catch(() => {});
  const loadReservations = () => {
    if (!activeBranch) return;

    api
      .reservations(activeBranch)
      .then((r) => setReservations(r.data))
      .catch(() => setReservations([]));
  };
  useEffect(() => {
    setTables(null);
    load();
    loadReservations();
  }, [activeBranch]);
  useEffect(() => subscribe("table.update", load), [subscribe, activeBranch]);

  const floors = useMemo(() => {
    if (!tables) return {};
    return tables.reduce((acc, t) => {
      (acc[t.floor] = acc[t.floor] || []).push(t);
      return acc;
    }, {});
  }, [tables]);

  const counts = useMemo(() => {
    const c = { available: 0, occupied: 0, reserved: 0 };
    (tables || []).forEach((t) => {
      c[t.status] = (c[t.status] || 0) + 1;
    });
    return c;
  }, [tables]);

  if (!tables)
    return (
      <div className="p-6">
        <Loader label="Loading floor plan" />
      </div>
    );
const formatReservationTime = (time) => {
  if (!time) return "";

  const [hours, minutes] = time.split(":").map(Number);

  const date = new Date();
  date.setHours(hours, minutes, 0, 0);

  return date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};
  return (
    <>
      <div className="p-4 sm:p-6 animate-fade-up">
        <PageHeader
          title="Floor & Tables"
          subtitle="Live view of your dining areas"
          actions={
            <div className="flex items-center gap-3">
              <div className="flex gap-3 text-sm">
                {Object.entries(STATUS_STYLE)
                  .slice(0, 3)
                  .map(([k, s]) => (
                    <span key={k} className="flex items-center gap-1.5">
                      <span className={cn("h-2.5 w-2.5 rounded-full", s.dot)} />
                      {s.label} {counts[k] || 0}
                    </span>
                  ))}
              </div>

              <Button
                onClick={() => {
                  setEditingTable(null);
                  setNewTable({
                    name: "",
                    floor: "Main Floor",
                    seats: 2,
                  });
                  setAddOpen(true);
                }}
                className="rounded-xl font-semibold"
              >
                <Plus size={16} className="mr-1" />
                Add Table
              </Button>
            </div>
          }
        />

        {tables.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-dashed border-border bg-secondary/30 p-10 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white border border-border">
              <Plus size={26} className="text-muted-foreground" />
            </div>

            <h3 className="font-display font-semibold text-lg mt-4">
              No tables added yet
            </h3>

            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              This branch does not have any dining tables yet. Add your first
              table using the{" "}
              <span className="font-semibold text-foreground">Add Table</span>{" "}
              button above to start managing seating and table orders.
            </p>
          </div>
        ) : (
          Object.entries(floors).map(([floor, ts]) => (
            <div key={floor} className="mb-8">
              <h3 className="font-display font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />

                {floor}

                <span className="text-xs font-normal text-muted-foreground">
                  ({ts.length})
                </span>
              </h3>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-3">
                {ts.map((t) => {
                  const s = STATUS_STYLE[t.status] || STATUS_STYLE.available;

                  const tableReservations = reservations.filter(
                    (r) => r.table_id === t.id,
                  );

                  const today = new Date();

                  const todayDate = `${today.getFullYear()}-${String(
                    today.getMonth() + 1,
                  ).padStart(2, "0")}-${String(today.getDate()).padStart(
                    2,
                    "0",
                  )}`;

                  const todayReservation = tableReservations
                    .filter(
                      (r) => r.date === todayDate && r.status === "confirmed",
                    )
                    .sort((a, b) => a.time.localeCompare(b.time))[0];

                  return (
                    <button
                      key={t.id}
                      data-testid={`table-${t.id}`}
                      onClick={() => navigate(`/app/tables/${t.id}`)}
                      className={cn(
                        "relative rounded-2xl border-2 p-4 text-left transition-all hover:shadow-[0_8px_24px_rgba(28,25,23,0.1)] active:scale-[0.98]",
                        s.ring,
                        s.bg,
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-display text-xl font-bold">
                          {t.name}
                        </span>

                        <span
                          className={cn(
                            "h-3 w-3 rounded-full",
                            s.dot,
                            t.status === "occupied" && "pulse-ring",
                          )}
                        />
                      </div>

                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                        <Users size={13} />
                        {t.seats} seats
                      </div>

                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center min-h-8">
                          {t.amount > 0 ? (
                            <div className="flex items-center gap-1 text-sm font-bold text-foreground">
                              <Receipt size={13} />
                              {money(t.amount)}
                            </div>
                          ) : (
                            <div
                              className={cn(
                                "text-xs font-bold uppercase tracking-wide",
                                s.text,
                              )}
                            >
                              {s.label}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-0">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();

                              setEditingTable(t);

                              setNewTable({
                                name: t.name || "",
                                floor: t.floor || "Main Floor",
                                seats: t.seats || 2,
                              });

                              setAddOpen(true);
                            }}
                            className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-secondary"
                            title="Edit table"
                          >
                            <Pencil size={14} />
                          </button>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTable(t);
                            }}
                            className="h-8 w-8 rounded-lg flex items-center justify-center text-red-500 hover:bg-red-50"
                            title="Delete table"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>

                      {todayReservation && (
                        <div className="mt-2 rounded-lg bg-violet-50 px-2.5 py-1.5 text-xs">
                          <div className="font-semibold text-violet-700">
                            Reserved for {todayReservation.customer_name}
                          </div>

                          <div className="text-violet-600">
                            {formatReservationTime(todayReservation.time)} ·{" "}
                            {todayReservation.guests} guests
                          </div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editingTable ? "Edit Table" : "Add New Table"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <Input
              placeholder="Table name (e.g. T9)"
              value={newTable.name}
              onChange={(e) =>
                setNewTable({
                  ...newTable,
                  name: e.target.value,
                })
              }
              className="h-11 rounded-xl"
            />

            <Input
              placeholder="Floor"
              value={newTable.floor}
              onChange={(e) =>
                setNewTable({
                  ...newTable,
                  floor: e.target.value,
                })
              }
              className="h-11 rounded-xl"
            />

            <Input
              type="number"
              min="1"
              placeholder="Seats"
              value={newTable.seats}
              onChange={(e) =>
                setNewTable({
                  ...newTable,
                  seats: e.target.value,
                })
              }
              className="h-11 rounded-xl"
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-xl"
              onClick={() => setAddOpen(false)}
              disabled={adding}
            >
              Cancel
            </Button>

            <Button
              className="rounded-xl font-semibold"
              onClick={saveTable}
              disabled={adding}
            >
              {adding
                ? editingTable
                  ? "Saving..."
                  : "Adding..."
                : editingTable
                  ? "Save Changes"
                  : "Add Table"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={!!deleteTable}
        title="Delete Table"
        message={
          deleteTable
            ? `Are you sure you want to delete ${deleteTable.name}? This action cannot be undone.`
            : ""
        }
        confirmText="Delete Table"
        cancelText="Cancel"
        loading={deleteLoading}
        onCancel={() => {
          if (!deleteLoading) {
            setDeleteTable(null);
          }
        }}
        onConfirm={async () => {
          if (!deleteTable) return;

          try {
            setDeleteLoading(true);

            await api.deleteTable(deleteTable.id);

            toast.success(`${deleteTable.name} deleted`);

            setDeleteTable(null);

            await load();
          } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to delete table");
          } finally {
            setDeleteLoading(false);
          }
        }}
      />
    </>
  );
}
